import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, XCircle, Undo2 } from "lucide-react";
import { api, dayLabel, downloadFile, errMsg, MEDIA_LABEL } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { SearchInput, normText } from "@/components/fly/FilterSelect";

const STATUS = { pending: ["Oczekuje", "text-amber-300"], approved: ["Zatwierdzone", "text-emerald-400"], rejected: ["Odrzucone", "text-red-400"] };
const KIND = { music: "Muzyka", photo: "Zdjęcie", video: "Animacja" };

const FileBtn = ({ f, label, tid }) => (f ? (
  <button data-testid={tid} onClick={() => downloadFile({ path: `/admin/uploads/${f.id}/download.file`, filename: f.filename }).catch(() => toast.error("Nie udało się pobrać pliku"))}
    className="h-8 px-2 rounded-lg border border-blue-500/30 text-blue-200 hover:bg-blue-500/10 text-xs inline-flex items-center gap-1 max-w-full">
    <Download className="w-3 h-3 shrink-0" /><span className="truncate">{label}</span>
  </button>
) : null);

export const SignupsTab = ({ onChange, settings }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = () => api.get("/admin/signups").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const n = normText(q.trim());
    return items.filter((s) => (!status || s.status === status) && (!n || normText(`${s.number} ${s.contact_name} ${s.school} ${s.category} ${s.team_name} ${s.members.map((m) => m.name).join(" ")}`).includes(n)));
  }, [items, q, status]);

  const update = async (s, body, msg) => {
    try { await api.put(`/admin/signups/${s.id}`, body); toast.success(msg); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  const ended = !!settings?.signup_deadline && new Date(settings.signup_deadline) < new Date();
  const exportCsv = () => downloadFile({ path: "/admin/signups.csv", filename: "zapisy-fly-cup.csv" }).catch(() => toast.error("Nie udało się pobrać pliku"));

  return (
    <div className="grid gap-4" data-testid="signups-tab">
      <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between ${ended ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-[#0B132B]/60"}`} data-testid="signups-export-box">
        <div className="text-sm">
          {ended ? <b className="text-emerald-300" data-testid="signups-ended-info">Zapisy zakończone – pobierz plik CSV ze wszystkimi zgłoszeniami.</b>
            : <span className="text-slate-300">Eksport wszystkich zgłoszeń do pliku CSV (Excel).</span>}
        </div>
        <button data-testid="signups-export-csv" onClick={exportCsv} className={`h-10 px-4 rounded-lg text-sm font-semibold inline-flex items-center gap-2 shrink-0 ${ended ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "border border-blue-500/30 text-blue-200 hover:bg-blue-500/10"}`}>
          <Download className="w-4 h-4" />Pobierz CSV zapisów ({items.length})
        </button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1"><SearchInput testid="signups-search" value={q} onChange={setQ} placeholder="Szukaj: numer, osoba, klub, kategoria…" /></div>
        <select data-testid="signups-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-12 px-3 rounded-xl bg-[#0B132B] border border-blue-500/20 text-sm">
          <option value="">Wszystkie ({items.length})</option>
          {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l} ({items.filter((s) => s.status === k).length})</option>)}
        </select>
      </div>
      {!shown.length && <p className="text-slate-400" data-testid="signups-empty">Brak zgłoszeń.</p>}
      {shown.map((s) => <SignupCard key={s.id} s={s} update={update} />)}
    </div>
  );
};

const SignupCard = ({ s, update }) => {
  const [label, color] = STATUS[s.status];
  return (
    <section className="fly-card p-4 sm:p-5 min-w-0" data-testid={`signup-card-${s.number}`}>
      <div className="flex flex-col lg:flex-row lg:items-start gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-blue-300">{s.number}</span>
            <span className={`text-xs font-semibold ${color}`} data-testid={`signup-status-${s.number}`}>{label}{s.day && s.status === "approved" ? ` · ${dayLabel(s.day)}` : ""}</span>
            <span className="text-xs text-slate-500">{new Date(s.created_at).toLocaleString("pl-PL")}</span>
          </div>
          <h3 className="font-display text-xl font-bold uppercase mt-1">{s.team_name || s.members.map((m) => m.name).join(" i ")}</h3>
          <p className="text-sm text-blue-200">{s.category} · {s.age_group}</p>
          <p className="text-xs text-slate-400 mt-1">{s.school || "Niezrzeszony"}{s.instructor && ` · instr. ${s.instructor}`} · zgłasza: {s.contact_name}, {s.email}, {s.phone}</p>
          {s.notes && <p className="text-xs text-slate-300 mt-1 italic">„{s.notes}”</p>}
        </div>
        <div className="flex flex-col gap-2 lg:items-end shrink-0">
          <div className="text-sm">Opłata: <b className="text-amber-300">{s.fee} zł</b></div>
          <label className="flex items-center gap-2 text-xs"><Switch data-testid={`signup-paid-${s.number}`} checked={s.paid} onCheckedChange={(v) => update(s, { paid: v }, v ? "Oznaczono jako opłacone" : "Oznaczono jako nieopłacone")} />{s.paid ? "Opłacone" : "Nieopłacone"}</label>
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-[#0B132B]/60 border border-white/5 p-3 text-xs grid gap-1">
          <b className="text-slate-300">Zawodnicy i zgody</b>
          {s.members.map((m, i) => (
            <div key={i} className="flex items-center gap-2 justify-between"><span>{m.name} ({m.birth_year})</span>
              <span className="flex flex-wrap gap-1 justify-end">{(m.consents || []).map((c, j) => <FileBtn key={c.id} f={c} label={(m.consents.length > 1 ? `zgoda ${j + 1}` : "zgoda")} tid={`signup-consent-dl-${s.number}-${i}-${j}`} />)}</span></div>
          ))}
        </div>
        <div className="rounded-xl bg-[#0B132B]/60 border border-white/5 p-3 text-xs grid gap-2 content-start">
          <b className="text-slate-300">Multimedia: <span className="text-amber-300">{MEDIA_LABEL[s.media]}</span></b>
          <div className="flex flex-wrap gap-2">{Object.entries(s.file_info).map(([k, f]) => <FileBtn key={k} f={f} label={`${KIND[k]}: ${f?.filename}`} tid={`signup-file-${s.number}-${k}`} />)}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        {s.status !== "approved" && (
          <button data-testid={`signup-approve-${s.number}`} onClick={() => update(s, { status: "approved" }, "Zatwierdzono – przypisz dzień w zakładce Plan zawodów")} className="h-10 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Zatwierdź</button>
        )}
        {s.status !== "rejected" && <button data-testid={`signup-reject-${s.number}`} onClick={() => update(s, { status: "rejected" }, "Zgłoszenie odrzucone")} className="h-10 px-3 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />Odrzuć</button>}
        {s.status !== "pending" && <button data-testid={`signup-pending-${s.number}`} onClick={() => update(s, { status: "pending" }, "Przywrócono do oczekujących")} className="h-10 px-3 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 text-sm flex items-center gap-2"><Undo2 className="w-4 h-4" />Do oczekujących</button>}
      </div>
    </section>
  );
};
